import { useEffect, useState } from 'react'
import styled from 'styled-components'
import Avatar from 'components/Avatar'
import { SkeletonRows } from 'components/Skeleton'
import useDebounce from 'utils/hooks/useDebounce'
import { searchAssignableUsers } from 'services/taskService'
import Popover from './Popover'
import { ownerOption } from './ownerDisplay'
import { ACCENT_SOFT, BORDER, INK, MUTED } from './ui'

const OwnerPicker = ({ anchorEl, open, onClose, onSelect, buildingPassportId = null, currentOwnerId = null, clearLabel = null, title = 'Choose who is responsible' }) => {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 250)
  const [people, setPeople] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    setLoading(true)
    searchAssignableUsers({ search: debouncedQuery || undefined, buildingPassportId: buildingPassportId || undefined })
      .then(res => { if (!cancelled) setPeople((res.data?.data || []).map(ownerOption)) })
      .catch(() => { if (!cancelled) setPeople([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, debouncedQuery, buildingPassportId])

  return (
    <Popover anchorEl={anchorEl} open={open} onClose={onClose} label={title} width={360} closeLabel='Close without choosing anyone'>
      <Title>{title}</Title>
      <Help>
        {buildingPassportId
          ? 'Only people with access to this building are listed.'
          : 'People with access to your buildings. Anyone without access to a remedial’s building will be skipped for that remedial.'}
      </Help>
      <Search
        type='search'
        autoFocus
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder='Search by name'
        aria-label='Search people by name'
      />
      <List role='listbox' aria-label={title}>
        {clearLabel && (
          <Option type='button' role='option' aria-selected={!currentOwnerId} onClick={() => onSelect(null)}>
            <Name>{clearLabel}</Name>
          </Option>
        )}
        {loading && people.length === 0 && <SkeletonRows rows={3} widths={['70%']} />}
        {!loading && people.length === 0 && <Empty>No one matches that name.</Empty>}
        {people.map(person => (
          <Option
            key={person.id}
            type='button'
            role='option'
            aria-selected={person.id === currentOwnerId}
            data-user-id={person.id}
            onClick={() => onSelect(person)}
          >
            <Avatar src={person.avatar} firstName={person.firstName} lastName={person.lastName} email={person.email} size={32} />
            <Text>
              <Name>{person.name}</Name>
              {person.maskedEmail && <Sub>{person.maskedEmail}</Sub>}
            </Text>
            {person.id === currentOwnerId && <Current>Current owner</Current>}
          </Option>
        ))}
      </List>
    </Popover>
  )
}

export default OwnerPicker

const Title = styled.h4`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: ${INK};
`

const Help = styled.p`
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.45;
  color: ${MUTED};
`

const Search = styled.input`
  width: 100%;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  font: inherit;
  font-size: 15px;
  margin-bottom: 8px;
  box-sizing: border-box;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 6px 8px;
  border: none;
  border-radius: 8px;
  background: ${({ 'aria-selected': selected }) => (selected ? ACCENT_SOFT : 'transparent')};
  text-align: left;
  font: inherit;
  cursor: pointer;
  &:hover { background: ${ACCENT_SOFT}; }
`

const Text = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`

const Name = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
`

const Sub = styled.span`
  font-size: 14px;
  color: ${MUTED};
`

const Current = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${MUTED};
`

const Empty = styled.p`
  margin: 8px 4px;
  font-size: 15px;
  color: ${MUTED};
`
